import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { Customer, CustomerCredit, CustomerEventVoucher } from '../../../../../../../models/customer';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddCustomerCreditOrVoucherModalComponent } from './modals/add-customer-credit-or-voucher-modal/add-customer-credit-or-voucher-modal.component';
import { PurchaseCustomerEntryPassModalComponent } from '../memberships-and-passes-sub-list/modals/purchase-customer-entry-pass-modal/purchase-customer-entry-pass-modal.component';
import { PurchaseVoucherPackModalComponent } from './modals/purchase-voucher-pack-modal/purchase-voucher-pack-modal.component';

@Component({
  selector: 'app-credits-and-vouchers',
  templateUrl: './credits-and-vouchers-sub-list.component.html',
  styleUrls: ['./credits-and-vouchers-sub-list.component.css']
})
export class CreditsAndVouchersSubListComponent implements OnInit {

  public customerId: number;

  public customer: Customer;
  public creditsAndVouchers: CreditOrVoucher[] = [];

  public loadingCustomer = true;
  public loadingCreditsAndVouchers = true;

  public customerNotFound = false;
  public serverError: number = null;

  public successMessage = '';

  // Make the CreditOrVoucherStatus enum available to the template.
  public CreditOrVoucherStatus = CreditOrVoucherStatus;

  constructor(
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    const routeParams = this.route.parent.snapshot.params;
    this.customerId = parseInt(routeParams.id, 10);
    if (isNaN(this.customerId)) {
      this.customerNotFound = true;
      return;
    }

    this.loadCustomer();
    this.loadCreditsAndVouchers();
  }

  public getStatusString(status: CreditOrVoucherStatus): string {
    if (status === CreditOrVoucherStatus.active) { return 'Active'; }
    else if (status === CreditOrVoucherStatus.used) { return 'Used'; }
    else if (status === CreditOrVoucherStatus.removed) { return 'Removed'; }
    else if (status === CreditOrVoucherStatus.expired) { return 'Expired'; }
  }

  public removeCreditOrVoucher(creditOrVoucher: CreditOrVoucher) {
    creditOrVoucher.isLoading = true;
    creditOrVoucher.isRemoved = true;

    if (creditOrVoucher.credit) {
      creditOrVoucher.credit.is_removed = true;
      this.customerService
        .updateCustomerCredit(creditOrVoucher.credit)
        .then(() => {
          creditOrVoucher.status = getCreditStatus(creditOrVoucher.credit);
        })
        .catch(() => {
          creditOrVoucher.isRemoved = false;
          creditOrVoucher.credit.is_removed = false;
        })
        .finally(() => { creditOrVoucher.isLoading = false; });
    }
    if (creditOrVoucher.voucher) {
      creditOrVoucher.voucher.is_removed = true;
      this.customerService
        .updateCustomerEventVoucher(creditOrVoucher.voucher)
        .then(() => {
          creditOrVoucher.status = getVoucherStatus(creditOrVoucher.voucher);
        })
        .catch(() => {
          creditOrVoucher.isRemoved = false;
          creditOrVoucher.voucher.is_removed = false;
        })
        .finally(() => { creditOrVoucher.isLoading = false; });
    }
  }

  public restoreCreditOrVoucher(creditOrVoucher: CreditOrVoucher) {
    creditOrVoucher.isLoading = true;
    creditOrVoucher.isRemoved = false;

    if (creditOrVoucher.credit) {
      creditOrVoucher.credit.is_removed = false;
      this.customerService
        .updateCustomerCredit(creditOrVoucher.credit)
        .then(() => {
          creditOrVoucher.status = getCreditStatus(creditOrVoucher.credit);
        })
        .catch(() => {
          creditOrVoucher.isRemoved = true;
          creditOrVoucher.credit.is_removed = true;
        })
        .finally(() => { creditOrVoucher.isLoading = false; });
    }
    if (creditOrVoucher.voucher) {
      creditOrVoucher.voucher.is_removed = false;
      this.customerService
        .updateCustomerEventVoucher(creditOrVoucher.voucher)
        .then(() => {
          creditOrVoucher.status = getVoucherStatus(creditOrVoucher.voucher);
        })
        .catch(() => {
          creditOrVoucher.isRemoved = true;
          creditOrVoucher.voucher.is_removed = true;
        })
        .finally(() => { creditOrVoucher.isLoading = false; });
    }
  }

  public handleAddCreditOrVoucher() {
    this.successMessage = '';

    const modalRef = this.modalService.open(AddCustomerCreditOrVoucherModalComponent);

    modalRef.componentInstance.customerId = this.customerId;
    modalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.successMessage = successMessage;
      this.loadCreditsAndVouchers();
    }
  }

  public openPurchaseVoucherPackModal() {
    this.successMessage = '';

    const modalRef = this.modalService.open(
      PurchaseVoucherPackModalComponent
    );
    modalRef.componentInstance.customer = this.customer;
  }

  public getCreditRecurringPaymentString(credit: CustomerCredit) {
    if (!credit.will_apply_to_recurring_payments) {
      return 'Does not apply to recurring payments';
    } else {
      if (credit.only_applies_to_recurring_payments) {
        return 'Only applies to recurring payments';
      } else {
        return 'Applies to recurring payments';
      }
    }
  }

  private loadCustomer() {
    this.loadingCustomer = true;

    this.customerService
      .getCustomer(this.customerId)
      .then(customer => {
        this.customer = customer;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomer = false; });
  }

  private loadCreditsAndVouchers() {
    this.loadingCreditsAndVouchers = true;

    Promise
      .all([
        this.customerService.getCustomerCredits(
          this.customerId,
          1,
          100
        ),
        this.customerService.getCustomerEventVouchers(
          this.customerId,
          1,
          100
        )
      ])
      .then(results => {
        this.processCreditsAndVouchers(results[0].results, results[1].results);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCreditsAndVouchers = false; });
  }

  private processCreditsAndVouchers(credits: CustomerCredit[], vouchers: CustomerEventVoucher[]) {
    this.creditsAndVouchers = [];

    const activeCreditsAndVouchers: CreditOrVoucher[] = [];
    const inActiveCreditsAndVouchers: CreditOrVoucher[] = [];

    for (const credit of credits) {
      const status = getCreditStatus(credit);
      const creditOrVoucher: CreditOrVoucher = {
        credit,
        status,
        expiryDateStr: credit.expiry_date_str,
        isRemoved: credit.is_removed,
        createdById: credit.created_by_id,
        createdByFirstName: credit.created_by_first_name,
        createdByLastName: credit.created_by_last_name,
        createdByEmail: credit.created_by_email,
        isPurchased: false,
        dateAddedStr: credit.date_added_str,
        createdAt: credit.created_at,
        isLoading: false
      };
      if (status === CreditOrVoucherStatus.active) {
        activeCreditsAndVouchers.push(creditOrVoucher);
      } else {
        inActiveCreditsAndVouchers.push(creditOrVoucher);
      }
    }
    for (const voucher of vouchers) {
      const status = getVoucherStatus(voucher);
      const creditOrVoucher: CreditOrVoucher = {
        voucher,
        status,
        expiryDateStr: voucher.expiry_date_str,
        isRemoved: voucher.is_removed,
        createdById: voucher.created_by_id,
        createdByFirstName: voucher.created_by_first_name,
        createdByLastName: voucher.created_by_last_name,
        createdByEmail: voucher.created_by_email,
        isPurchased: voucher.invoice_id !== null,
        dateAddedStr: voucher.date_added_str,
        createdAt: voucher.created_at,
        isLoading: false
      };
      if (status === CreditOrVoucherStatus.active) {
        activeCreditsAndVouchers.push(creditOrVoucher);
      } else {
        inActiveCreditsAndVouchers.push(creditOrVoucher);
      }
    }

    const compareFunction = (
      (a: CreditOrVoucher, b: CreditOrVoucher) => a.createdAt < b.createdAt ? 1 : -1
    );
    activeCreditsAndVouchers.sort(compareFunction);
    inActiveCreditsAndVouchers.sort(compareFunction);

    this.creditsAndVouchers.push(...activeCreditsAndVouchers);
    this.creditsAndVouchers.push(...inActiveCreditsAndVouchers);
  }

}


enum CreditOrVoucherStatus {
  active,
  used,
  removed,
  expired
}


class CreditOrVoucher {
  credit?: CustomerCredit;
  voucher?: CustomerEventVoucher;
  status: CreditOrVoucherStatus;
  expiryDateStr?: string;
  isRemoved: boolean;
  createdById?: number;
  createdByFirstName?: string;
  createdByLastName?: string;
  createdByEmail?: string;
  isPurchased: boolean;
  dateAddedStr: string;
  createdAt: string;
  isLoading: boolean;
}


function getCreditStatus(credit: CustomerCredit): CreditOrVoucherStatus {
  if (credit.is_removed) {
    return CreditOrVoucherStatus.removed;
  } else if (credit.amount_remaining <= 0) {
    return CreditOrVoucherStatus.used;
  } else if (credit.is_expired) {
    return CreditOrVoucherStatus.expired;
  } else {
    return CreditOrVoucherStatus.active;
  }
}


function getVoucherStatus(voucher: CustomerEventVoucher): CreditOrVoucherStatus {
  if (voucher.is_removed) {
    return CreditOrVoucherStatus.removed;
  } else if (voucher.number_of_uses_remaining <= 0) {
    return CreditOrVoucherStatus.used;
  } else if (voucher.is_expired) {
    return CreditOrVoucherStatus.expired;
  } else {
    return CreditOrVoucherStatus.active;
  }
}
